import { memo, useCallback, useContext, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import UserReviewPagination from './UserReviewPagination'
import UserReviewTile from './UserReviewTile'
import LineFilterAltIcon from 'LuxKit/Icons/line/LineFilterAltIcon'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import useReviewLabels from 'hooks/useReviewLabels'
import type { UserReviewFiltersModalResult } from './UserReviewFiltersModal'
import UserReviewFiltersModal from './UserReviewFiltersModal'
import UserReviewSort from './UserReviewSort'
import UserReviewEmptyState from './UserReviewEmptyState'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import PropertyReviewAiSummary from 'components/OfferPage/NavigableOfferContent/PropertyReviewAiSummary'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import useUserReviewsSummary from 'hooks/Reviews/useUserReviewsSummary'
import useUserReviews from 'hooks/Reviews/useUserReviews'
import { sum } from 'lib/array/arrayUtils'
import ModalContext from 'contexts/ModalContext'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import type { ReviewCompanionshipType } from 'api/review'
import { useIntl } from 'react-intl'

const Root = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};
  position: relative;
`

const ReviewsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
`

const INITIAL_FILTERS: LocalFilters = { ratings: [], travellers: [], months: [] }

interface LocalFilters {
  ratings?: Array<string>
  travellers?: Array<ReviewCompanionshipType>
  months?: Array<string>
}

type ReviewSortType = 'newest' | 'oldest' | 'best_rating' | 'worst_rating' | 'most_relevant'

const pageSize = 4

interface Props {
  property?: App.OfferProperty | App.BedbankProperty
  isLPP?: boolean
  experience?: App.ExperienceOffer
  tour?: App.Tours.TourV2Offer
  showReviewFiltersAndUserDetails?: boolean
  reviewsThreshold?: number
}

function UserReviewList({
  property,
  isLPP,
  experience,
  tour,
  showReviewFiltersAndUserDetails = true,
  reviewsThreshold,
}: Props) {
  const { userReviewsSortOptions, SORT_OPTION_MOST_RELEVANT } = useReviewLabels()
  const rootRef = useRef<HTMLDivElement>(null)
  const [filters, setFilters] = useState<LocalFilters>(INITIAL_FILTERS)
  const [sort, setSort] = useState<ReviewSortType>(SORT_OPTION_MOST_RELEVANT.value as ReviewSortType)
  const [localPage, setLocalPage] = useState<number>(1)
  // As the user changes pages, we want to show the old reviews until it's done loading
  // so we'll store them here when this happens so we can display them
  const [oldReviews, setOldReviews] = useState<Array<App.Review>>([])
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const [reviewSummary, fetchingReviewSummary] = useUserReviewsSummary(
    property?.id,
    isLPP ? 'bedbank' : 'luxuryescapes',
  )

  const offset = (localPage - 1) * pageSize
  const [reviews, fetchingReviews] = useUserReviews({
    propertyProvider: isLPP ? 'bedbank' : 'luxuryescapes',
    companionship: filters.travellers,
    months: filters.months,
    ratings: filters.ratings,
    propertyId: property?.id,
    experienceId: experience?.id,
    tourId: tour?.id,
    useExternalReviews: true,
    useHiddenForProperty: true,
    includeTranslations: true,
    limit: pageSize,
    offset,
    sort,
  })

  const totalFilters = useMemo(() => {
    return sum(Object.values(filters), (item) => item.length)
  }, [filters])

  const displayableRating = property?.rating ?? experience?.rating ?? tour?.rating
  const isGoogleReviews = displayableRating?.reviewsSource === 'google'
  const showReviewSort = !isGoogleReviews
  const showReviewFilters = !isGoogleReviews
  const showReviewControls = showReviewSort || showReviewFilters
  const ratingSubtitle = isGoogleReviews
    ? intl.formatMessage({
        id: 'byU9brQf-edL75fFB_Ts',
        defaultMessage: 'Reviews from Google',
        description: 'Subtitle indicating that displayed reviews are sourced from Google',
      })
    : undefined

  const onSortChange = useCallback((newSort: ReviewSortType) => {
    setSort(newSort)
  }, [])

  const onResetFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS)
  }, [])

  const onShowFilters = useCallback(async() => {
    const newFilters = await showModal<UserReviewFiltersModalResult | undefined>(<UserReviewFiltersModal
      existingFilters={filters}
    />)

    if (newFilters) {
      setFilters(newFilters)

      // If user add some ratings filter, set sort to Best rating
      const newRatingFilters = newFilters.ratings ?? []
      const ratingFilters = filters.ratings ?? []
      if (newRatingFilters.length > ratingFilters.length && sort !== SORT_OPTION_MOST_RELEVANT.value) {
        setSort(SORT_OPTION_MOST_RELEVANT.value as ReviewSortType)
      }
    }
  }, [filters, showModal, sort, SORT_OPTION_MOST_RELEVANT.value])

  const onPageChange = useCallback((page: number) => {
    setLocalPage(page)
    setOldReviews(reviews?.reviews ?? [])
    // scroll us back to the top when they change pages as they'll probably want to read them!
    rootRef.current?.scrollIntoView()
  }, [reviews?.reviews])

  const visibleReviews = fetchingReviews ? oldReviews : reviews?.reviews ?? []
  const offerRatingDisplayable = isOfferRatingDisplayable(displayableRating, reviewsThreshold)

  const allFiltersLabel = totalFilters > 0
    ? intl.formatMessage(
        {
          id: 'FGVTa9fQq0JRHVGvZAvC',
          defaultMessage: 'All filters ({count})',
          description: 'Filter chip showing active filter count for user reviews',
        },
        { count: totalFilters },
      )
    : intl.formatMessage({
        id: 'hTZu_p4uUEJ8tC9rx3Ei',
        defaultMessage: 'All filters',
        description: 'Filter chip for user reviews with no active filters',
      })

  return (
    <Root
      ref={rootRef}
      id="customer-reviews"
    >
      <LoadingIndicator floating visible={fetchingReviews} />
      <Group
        direction="vertical"
        gap={24}
        tabletDirection="horizontal"
        tabletHorizontalAlign="space-between"
        tabletVerticalAlign="end"
      >
        {offerRatingDisplayable && <NumberRating
          variant="xl"
          rating={displayableRating.score}
          total={displayableRating.reviewsTotal}
          ratingSubtitle={ratingSubtitle}
        />}
        {showReviewControls && <Group direction="horizontal" gap={8}>
          {showReviewSort && <UserReviewSort
            sortOptions={userReviewsSortOptions}
            sortValue={sort}
            onSelect={onSortChange}
          />}
          {showReviewFilters && <AssistChip
            size="medium"
            variant="filled"
            startIcon={<LineFilterAltIcon />}
            onClick={onShowFilters}
          >
            {allFiltersLabel}
          </AssistChip>}
        </Group>}
      </Group>
      <Group direction="vertical" fullWidth gap={24}>
        {fetchingReviewSummary && <LoadingBox height={160} />}
        {reviewSummary?.summary && <PropertyReviewAiSummary content={reviewSummary.summary} />}
        {!reviews?.reviews.length && !fetchingReviews && (<UserReviewEmptyState
          onResetFilter={onResetFilters}
          showReviewFiltersAndUserDetails={showReviewFiltersAndUserDetails}
        />)}
        {!!visibleReviews.length && <UserReviewPagination
          label={intl.formatMessage({
            id: 'PhqYQBn7JA_4idEaQN-s',
            defaultMessage: 'Reviews',
            description: 'Label for the reviews pagination section in the reviews section',
          })}
          total={reviews?.total ?? 0}
          pageSize={pageSize}
          currentPage={localPage}
          onPageChange={onPageChange}
        >
          <ReviewsList>
            {visibleReviews.map(review => <UserReviewTile
              key={review.id}
              userReview={{
                ...review,
                experienceId: experience?.id,
                tourId: tour?.id,
              }}
              showReviewFiltersAndUserDetails={showReviewFiltersAndUserDetails}
            />)}
          </ReviewsList>
        </UserReviewPagination>}
      </Group>
    </Root>
  )
}

export default memo(UserReviewList)
